import { Add, Remove, UnfoldLess, UnfoldMore } from "@mui/icons-material";
import { Box, Button, IconButton, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material"
import { ReactNode, useEffect, useState } from "react";

export interface TableNode{
  children: TableNode[]; 
  [key: string]: any; 
}

export type HiearchicalTableColumn <T extends TableNode> = {
  displayName: string;
  id: string;
  render?: (row: T, level: number, parentRow?: T) => ReactNode;
};

export const HiearchicalTable = <T extends TableNode> ({
  rows,
  columnNames,
}: {
  rows: T,
  columnNames: HiearchicalTableColumn<T>[],
}) => {
  const [expandAll, setExpandAll] = useState<boolean | undefined>(undefined)

  return (
    <TableContainer>
      <Box sx={{display:"flex", flexDirection:"row", gap: "10px", marginBottom: "5px"}}>
        <Button variant="text" disabled={expandAll === false} startIcon={<UnfoldLess/>} onClick={() => setExpandAll(false)}>Collapse All</Button> 
        <Button variant="text" disabled={expandAll} startIcon={<UnfoldMore/>} onClick={() => setExpandAll(true)}>Expand all</Button>
      </Box>
      <Table>
        <TableHeader columnNames={columnNames} rows={rows}/>
        <TableBody>
          <ExpandableRow columnNames={columnNames} row={rows} level={0} expanded={expandAll} handleToggle={() => setExpandAll(undefined)} defaultExpanded={true}/>
        </TableBody>
      </Table>
    </TableContainer>
  )
}

const TableHeader = <T extends TableNode> ({
  rows, 
  columnNames, 
}:{
  columnNames: HiearchicalTableColumn<T>[], 
  rows: T, 
}) => {

  return (
    <TableHead>
      <>
        <TableCell/>
        {
          columnNames.map((col) => (
            <TableCell sx={{justifyContent:"flex-start", fontWeight:"bold"}}>
              {col.displayName}
            </TableCell>
          ))
        }
      </>
    </TableHead>
  )
}

const ExpandableRow = <T extends TableNode> ({
  columnNames, 
  row, 
  level,
  expanded,
  handleToggle,
  parent,
  defaultExpanded
}:{
  columnNames: HiearchicalTableColumn<T>[], 
  row: T, 
  level: number,
  expanded?: boolean
  handleToggle?: () => void,
  parent?: T,
  defaultExpanded?: boolean
}) => {
  const [open, setOpen] = useState(expanded ?? (defaultExpanded ?? false))

  useEffect(() => {
    setOpen(prev => expanded ?? prev);
  }, [expanded]);

  return (
    <>
      <TableRow sx ={{height: "73px", backgroundColor: `rgba(0, 0, 0, ${0.02* level})`}}>
        {
          row.children.length > 0 ? (
            <TableCell sx={{width: "100px", paddingLeft: `${16 + level * 20}px`}}>
              <IconButton
                aria-label="expand row"
                size="small"
                onClick={() => {
                  setOpen(!open)
                  if(handleToggle) handleToggle()
                }}
              >
                {open ? <Remove /> : <Add />}
              </IconButton>
            </TableCell>
          ): <TableCell/>
        }
        {
          columnNames.map((col, i) => (
            <TableCell sx={{paddingLeft: i === 0 ? `${16 + level * 20}px` : "16px"}}> 
              {col.render ? col.render(row, level, parent) : row[col.id]}
            </TableCell>
          ))
        }
      </TableRow>
      {
        open && row.children.length > 0 && row.children.map((childRow) => (
          <ExpandableRow columnNames={columnNames} row={childRow as T} level={level +1} expanded={expanded} handleToggle={handleToggle} parent={row}/>
        ))
      }
    </>
  )
}